import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Package, Settings, TrendingUp, LogOut, Briefcase, Database,
  Bell, UserPlus, ShoppingCart, ChevronRight, X, Clock, Euro, Users,
  ClipboardList, MessageCircle, Wrench, BarChart3, Building2, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import MessageBanner from '../components/MessageBanner';
import BusinessCardModal from '../components/BusinessCardModal';
import { formatPrice } from '../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user?.role === 'master') {
      fetchNotifications();
      // Refresh every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      // Cache-bust + no-cache headers per Safari iOS
      const { data } = await axios.get(`${API}/api/notifications?_t=${Date.now()}`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      setNotifications(data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Definizione delle app disponibili
  const allApps = [
    {
      id: 'prodotti',
      gruppo: 'Commerciale',
      name: 'Prodotti',
      description: 'Anagrafiche e immagini',
      icon: Database,
      color: 'bg-purple-500',
      path: '/prodotti',
      permission: 'prodotti'
    },
    {
      id: 'inventario',
      gruppo: 'Vendita',
      name: 'Inventario',
      description: 'Conta articoli multi-luogo',
      icon: ClipboardList,
      color: 'bg-indigo-500',
      path: '/inventario',
      permission: 'inventario'
    },
    {
      id: 'clienti',
      gruppo: 'Commerciale',
      name: 'Clienti',
      description: 'Master B2B multi-azienda',
      icon: Users,
      color: 'bg-teal-500',
      path: '/clienti',
      permission: 'clienti'
    },
    {
      id: 'cataloghi',
      gruppo: 'Commerciale',
      name: 'Cataloghi',
      description: 'Gestione cataloghi prodotti',
      icon: Package,
      color: 'bg-blue-500',
      path: '/cataloghi',
      permission: 'cataloghi'
    },
    {
      id: 'vendite',
      gruppo: 'Vendita',
      name: 'Shop',
      description: 'Gestione shop',
      icon: TrendingUp,
      color: 'bg-green-500',
      path: '/vendite',
      permission: 'vendite'
    },
    {
      id: 'bigspin',
      gruppo: 'Vendita',
      name: 'BigSpin',
      description: 'Sito B2C — www.bigspin.it',
      icon: ShoppingCart,
      color: 'bg-fuchsia-600',
      path: '/bigspin',
      permission: 'bigspin'
    },
    {
      id: 'b2b',
      gruppo: 'Vendita',
      name: 'BigSpin B2B',
      description: 'Sito rivenditori — b2b.clabnet.it',
      icon: Building2,
      color: 'bg-blue-900',
      path: '/b2b',
      permission: 'b2b'
    },
    {
      id: 'contabilita',
      gruppo: 'Amministrazione',
      name: 'Contabilità',
      description: 'Prima nota, scadenzario, bilancio, fatturazione',
      icon: Briefcase,
      color: 'bg-amber-500',
      path: '/contabilita',
      permission: 'contabilita'
    },
    {
      id: 'credit',
      gruppo: 'Amministrazione',
      name: 'Credit',
      description: 'Gestione crediti aziende clienti',
      icon: Euro,
      color: 'bg-rose-500',
      path: '/credit',
      permission: 'credit'
    },
    {
      id: 'posta',
      gruppo: 'Amministrazione',
      name: 'Posta',
      description: 'Leggi, rispondi, scrivi',
      icon: Mail,
      color: 'bg-sky-600',
      path: '/posta',
      permission: 'comunicazioni'
    },
    {
      id: 'chat',
      gruppo: 'Strumenti',
      name: 'Assistente',
      description: 'Chat AI sui tuoi dati',
      icon: MessageCircle,
      color: 'bg-indigo-600',
      path: '/chat',
      permission: 'chat'
    },
    {
      id: 'utility',
      gruppo: 'Strumenti',
      name: 'Utility',
      description: 'Calcolatrici e convertitori file',
      icon: Wrench,
      color: 'bg-slate-600',
      path: '/utility',
      permission: 'utility'
    },
    {
      id: 'report',
      gruppo: 'Amministrazione',
      name: 'Report',
      description: 'Report ed export di dati',
      icon: BarChart3,
      color: 'bg-cyan-600',
      path: '/report',
      permission: 'report'
    },
    {
      id: 'pxp',
      gruppo: 'Commerciale',
      name: 'PXP',
      description: 'Gestione azienda PXP',
      icon: Building2,
      color: 'bg-slate-800',
      path: '/pxp',
      permission: 'pxp'
    },
    {
      id: 'admin',
      gruppo: 'Strumenti',
      name: 'Admin',
      description: 'Pannello di controllo',
      icon: Settings,
      color: 'bg-gray-700',
      path: '/admin',
      permission: 'admin'
    }
  ];

  // Filtra le app in base ai permessi dell'utente
  const userApps = allApps.filter(app => {
    if (user?.role === 'master') return true;
    if (!user?.permissions) return false;
    return user.permissions.includes(app.permission);
  });

  const handleAppClick = (app) => {
    if (app.comingSoon) return;
    navigate(app.path);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString('it-IT');
  };

  const totalNotifications = notifications 
    ? (notifications.pending_users?.length || 0) + (notifications.pending_orders?.length || 0)
    : 0;

  return (
    <div className="min-h-svh bg-[#F2F2F7]">
      {/* Header — stessa griglia delle app (3 col mobile, 4 col desktop):
          il logo si allinea sopra la prima colonna ("Prodotti"), il banner
          occupa le colonne centrali, i controlli sopra l'ultima. */}
      <header className="pt-[calc(1rem+env(safe-area-inset-top))] md:pt-[calc(2rem+env(safe-area-inset-top))] pb-2 md:pb-4 px-4 md:px-8">
        <div className="max-w-4xl mx-auto grid grid-cols-4 gap-x-2 md:gap-6 items-center justify-items-center">
          {/* Col 1: logo (cliccabile → biglietto), allineato come un'icona app */}
          <div className="w-full flex justify-center">
            <button
              onClick={() => setShowCard(true)}
              className="flex-shrink-0 rounded-[22%] transition-transform duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              title="Biglietto da visita C-LAB"
              data-testid="business-card-btn"
            >
              <img src="/logo-clab.svg?v=6" alt="C-LAB" className="h-[60px] w-[60px] rounded-[22%] shadow-[0_1px_3px_rgba(0,0,0,0.15)] md:h-20 md:w-20" />
            </button>
          </div>

          {/* Col 2 (mobile) / Col 2-3 (desktop): banner messaggi (centrale) */}
          <div className="col-span-2 flex items-center justify-center min-w-0">
            <MessageBanner />
          </div>

          {/* Ultima col: campanella + logout, allineati a destra come l'icona app */}
          <div className="w-full flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              {user?.role === 'master' && (
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-xl hover:bg-white/50 transition-colors"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-6 h-6 text-gray-600" />
                  {totalNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {totalNotifications > 9 ? '9+' : totalNotifications}
                    </span>
                  )}
                </button>
              )}
              <button
                onClick={async () => {
                  await logout();
                  navigate('/login');
                }}
                className="p-2 rounded-xl hover:bg-white/50 transition-colors"
                title="Esci"
                data-testid="logout-btn"
              >
                <LogOut className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-xs font-medium text-gray-600 truncate max-w-[150px]" title={user?.email}>{user?.email}</p>
              <p className="text-[11px] text-gray-500">{formatDate(currentTime)}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Biglietto da visita C-LAB (QR vCard) */}
      {showCard && <BusinessCardModal onClose={() => setShowCard(false)} />}

      {/* Notifications Dropdown */}
      {showNotifications && user?.role === 'master' && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setShowNotifications(false)}>
          <div 
            className="w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Notifiche</h2>
              <button onClick={() => setShowNotifications(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Pending Users */}
              {notifications?.pending_users?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="w-5 h-5 text-orange-500" />
                    <h3 className="font-semibold text-gray-900">Nuovi utenti ({notifications.pending_users.length})</h3>
                  </div>
                  <div className="space-y-2">
                    {notifications.pending_users.map((u, i) => (
                      <div 
                        key={i}
                        onClick={() => { setShowNotifications(false); navigate('/admin'); }}
                        className="bg-orange-50 border border-orange-200 rounded-xl p-3 cursor-pointer hover:bg-orange-100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{u.name || 'Nuovo utente'}</p>
                            <p className="text-sm text-gray-500">{u.email}</p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(u.created_at)}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs text-orange-600 font-medium">In attesa di approvazione</span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Orders */}
              {notifications?.pending_orders?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingCart className="w-5 h-5 text-blue-500" />
                    <h3 className="font-semibold text-gray-900">Nuovi ordini ({notifications.pending_orders.length})</h3>
                  </div>
                  <div className="space-y-2">
                    {notifications.pending_orders.map((o, i) => (
                      <div 
                        key={i}
                        onClick={() => { setShowNotifications(false); navigate('/cataloghi'); }}
                        className="bg-blue-50 border border-blue-200 rounded-xl p-3 cursor-pointer hover:bg-blue-100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{o.cliente_nome}</p>
                            <p className="text-sm text-gray-500">{o.catalog_nome}</p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(o.created_at)}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-sm font-semibold text-blue-600 whitespace-nowrap tabular-nums">
                            {o.totale_pezzi} pz • {formatPrice(o.totale_importo)}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No notifications */}
              {totalNotifications === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nessuna notifica</p>
                </div>
              )}

              {/* Quick actions */}
              {totalNotifications > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <Button 
                    onClick={() => { setShowNotifications(false); navigate('/admin'); }}
                    variant="outline"
                    className="w-full"
                  >
                    Vai all'Admin Panel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="px-4 md:px-8 py-4 md:py-12">
        <div className="max-w-4xl mx-auto">

          {/* Notification Banner for Master */}
          {user?.role === 'master' && totalNotifications > 0 && (
            <div 
              onClick={() => setShowNotifications(true)}
              className="mb-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-4 cursor-pointer hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Bell className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold">Hai {totalNotifications} notifiche</p>
                    <p className="text-sm text-white/80">
                      {notifications?.pending_users?.length > 0 && `${notifications.pending_users.length} utenti in attesa`}
                      {notifications?.pending_users?.length > 0 && notifications?.pending_orders?.length > 0 && ' • '}
                      {notifications?.pending_orders?.length > 0 && `${notifications.pending_orders.length} ordini da evadere`}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6" />
              </div>
            </div>
          )}

          {/* App raggruppate per area, un colore per gruppo (non per app); verde,
              ambra e rosso restano agli stati (palette Impeccable 2026-11-04). */}
          <div className="space-y-6 md:space-y-8">
            {[
              ['Vendita', 'bg-gradient-to-b from-[#5AC8DC] to-[#30B0C7]'],
              ['Commerciale', 'bg-gradient-to-b from-[#3D9BFF] to-[#007AFF]'],
              ['Amministrazione', 'bg-gradient-to-b from-[#FFB340] to-[#FF9500]'],
              ['Strumenti', 'bg-gradient-to-b from-[#AEAEB2] to-[#8E8E93]'],
            ].map(([gruppo, tinta]) => {
              const apps = userApps.filter((a) => a.gruppo === gruppo);
              if (!apps.length) return null;
              return (
                <section key={gruppo}>
                  <h2 className="mb-3 px-1 text-[13px] font-semibold uppercase tracking-wide text-[#8E8E93]">{gruppo}</h2>
                  <div className="grid grid-cols-4 gap-x-2 gap-y-4 md:gap-6">
                    {apps.map((app) => {
                      const IconComponent = app.icon;
                      return (
                        <button
                          key={app.id}
                          onClick={() => handleAppClick(app)}
                          disabled={app.comingSoon}
                          data-testid={`app-${app.id}`}
                          className={`group flex min-w-0 flex-col items-center rounded-2xl px-1 py-1 transition-colors ${
                            app.comingSoon ? 'cursor-not-allowed opacity-50' : 'active:opacity-70'
                          }`}
                        >
                          <div className={`mb-1.5 flex h-[60px] w-[60px] items-center justify-center rounded-[22%] text-white shadow-[0_1px_3px_rgba(0,0,0,0.15)] transition-transform group-hover:scale-105 md:mb-2 md:h-20 md:w-20 ${tinta}`}>
                            <IconComponent className="h-8 w-8 md:h-10 md:w-10" strokeWidth={1.75} />
                          </div>
                          <span className="line-clamp-2 text-center text-[12px] font-medium leading-tight text-[#1C1C1E] md:text-sm">{app.name}</span>
                          {app.comingSoon && (
                            <span className="mt-1 text-[11px] text-[#5B6474]">Prossimamente</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>

        </div>
      </main>

    </div>
  );
}
